import React from 'react';
import PropTypes from 'prop-types';
import { FiDatabase, FiRefreshCw, FiSearch, FiX } from 'react-icons/fi';
import { Button } from 'components/common';
import { FILTERS } from './constants';

// Search, the status filters, and the two global actions. The filters are the
// "where do I still have work?" control: Not set is what an unfinished setup
// looks like, Overridden is what this environment has been changed away from
// its .env, Unsaved is what you are holding right now.

const Chip = ({ active, label, count, hint, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    title={hint}
    aria-pressed={active}
    className={[
      'tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[12px] tw-font-medium',
      'tw-border tw-cursor-pointer tw-transition-colors',
      active
        ? 'tw-bg-slate-900 tw-text-white tw-border-slate-900'
        : 'tw-bg-white tw-text-slate-600 tw-border-slate-200 hover:tw-bg-slate-50 hover:tw-text-slate-800',
    ].join(' ')}
  >
    {label}
    <span className={`tw-text-[11px] tw-tabular-nums ${active ? 'tw-text-white/70' : 'tw-text-slate-400'}`}>
      {count}
    </span>
  </button>
);

Chip.propTypes = {
  active: PropTypes.bool,
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  hint: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};

const SettingsToolbar = ({
  search, onSearchChange, searchRef, filter, onFilterChange, counts,
  shown, total, loading, seeding, hasPending, onRefresh, onSeed,
}) => (
  <div className="tw-flex tw-flex-col tw-gap-3 tw-min-w-0">
    <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
      <div className="tw-relative tw-flex-1 tw-basis-full sm:tw-basis-auto" style={{ maxWidth: 380 }}>
        <FiSearch size={13} className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400 tw-pointer-events-none" />
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search key, label or description"
          aria-label="Search settings"
          className="tw-w-full tw-pl-8 tw-pr-14 tw-py-2 tw-text-[13px] tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-text-slate-700 tw-outline-none focus:tw-border-primary-400 focus:tw-ring-2 focus:tw-ring-primary-100 tw-transition-colors placeholder:tw-text-slate-400"
        />
        {search ? (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            aria-label="Clear search"
            className="tw-absolute tw-right-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400 hover:tw-text-slate-600 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-p-0"
          >
            <FiX size={14} />
          </button>
        ) : (
          <kbd className="tw-absolute tw-right-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-[10px] tw-font-sans tw-text-slate-400 tw-border tw-border-slate-200 tw-rounded tw-px-1.5 tw-py-0.5 tw-bg-slate-50">
            /
          </kbd>
        )}
      </div>

      <span className="tw-text-[12px] tw-text-slate-400 tw-tabular-nums tw-shrink-0">
        {shown} of {total}
      </span>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 sm:tw-ml-auto">
        <Button
          size="sm"
          variant="secondary"
          onClick={onRefresh}
          disabled={loading || hasPending}
          title={hasPending ? 'Save or discard your changes first' : 'Reload from the server'}
        >
          <FiRefreshCw size={12} className={loading ? 'tw-animate-spin' : ''} /> Refresh
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={onSeed}
          loading={seeding}
          title="Add any key defined in the schema or .env that is missing here. Never overwrites a value."
        >
          <FiDatabase size={12} /> Seed from .env
        </Button>
      </div>
    </div>

    <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap">
      {FILTERS.map((f) => (
        <Chip
          key={f.key}
          label={f.label}
          hint={f.hint}
          count={counts[f.key] || 0}
          active={filter === f.key}
          onClick={() => onFilterChange(f.key === filter ? 'all' : f.key)}
        />
      ))}
    </div>
  </div>
);

SettingsToolbar.propTypes = {
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  searchRef: PropTypes.object,
  filter: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  counts: PropTypes.objectOf(PropTypes.number).isRequired,
  shown: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  seeding: PropTypes.bool,
  hasPending: PropTypes.bool,
  onRefresh: PropTypes.func.isRequired,
  onSeed: PropTypes.func.isRequired,
};

export default SettingsToolbar;
